import { ref } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";
import Vue from "vue";

import useCreatorShopApi from "@/store/creator-shop-posts/-api";
import {
  CreatorShopPostsStore,
  PostSearchFeed,
  PostSearchFeeds,
  PostSearchMap,
  PostSearchMeta,
} from "@/store/creator-shop-posts/types";
import mapData from "@/utils/transform/map-data";

const DEFAULT_META = { nbHits: 0, nbPages: 0, page: 0 };
const DEFAULT_FETCH_STATE = { pending: false, error: null, timestamp: 0 };
const emptyFeedFactory: () => PostSearchFeed = () => ({ ids: [], meta: DEFAULT_META });

export const useCreatorShopPostsStore = defineStore(
  "creator-shop-posts",
  () => {
    const posts = ref<PostSearchMap>({});
    const fetchState = ref<Vue["$fetchState"]>(DEFAULT_FETCH_STATE);
    const postSearchFeedMap = ref<PostSearchFeeds>({});

    const getFeed = (feed: string) => {
      return postSearchFeedMap.value?.[feed] ?? emptyFeedFactory();
    };

    const setSearchFeed = ({ feed, ids, meta }: { feed: string; ids: string[]; meta: PostSearchMeta }) => {
      postSearchFeedMap.value = Object.assign({}, postSearchFeedMap.value, { [feed]: { ids, meta } });
    };

    const updateSearchFeed = ({ feed, ids, meta }: { feed: string; ids: string[]; meta: PostSearchMeta }) => {
      postSearchFeedMap.value = Object.assign({}, postSearchFeedMap.value, {
        [feed]: {
          ids: [...postSearchFeedMap.value[feed].ids, ...ids],
          meta,
        },
      });
    };

    const getHeroAspectRatio = (postId: string): number => {
      const width = posts.value?.[postId]?.heroImageWidth || 1;
      const height = posts.value?.[postId]?.heroImageHeight || 1;
      return width / height;
    };

    const feedHasMorePages = (name: string) => {
      const feed = getFeed(name);
      // Results from the search endpoint are a 0 based page index
      return feed.meta.nbPages > 0 && feed.meta.page < feed.meta.nbPages - 1;
    };

    const fetchMoreBySearchTerm: CreatorShopPostsStore["fetchMoreBySearchTerm"] = async function (
      profileId: string,
      searchTerm: string,
    ) {
      if (!searchTerm || !profileId || !feedHasMorePages(searchTerm)) {
        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
        return false;
      }

      try {
        fetchState.value = { pending: true, error: null, timestamp: Date.now() };
        const meta = getFeed(searchTerm).meta;

        const response = await this.$api.get({
          query: searchTerm,
          profileId,
          ranking: "relevant",
          page: meta.page + 1,
        });

        const { nbHits, nbPages, page } = response?.data || {};

        if (response.data.hits.length > 0) {
          const fetchedAt = Date.now();
          const hits = response.data.hits.map((post) => ({ ...post, fetchedAt }));
          const meta = { nbHits, nbPages, page };
          const mappedPosts = mapData("objectId", hits);
          const postIds = hits.map(({ objectId }) => objectId);
          posts.value = Object.assign({}, posts.value, mappedPosts);
          updateSearchFeed({ feed: searchTerm, ids: postIds, meta });
        }

        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
      } catch (error) {
        fetchState.value = { pending: false, error: error as Error, timestamp: Date.now() };
        throw error;
      }
    };

    const fetchBySearchTerm: CreatorShopPostsStore["fetchBySearchTerm"] = async function (
      profileId: string,
      searchTerm: string,
    ) {
      if (!searchTerm || !profileId) {
        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
        return false;
      }

      try {
        fetchState.value = { pending: true, error: null, timestamp: Date.now() };

        const response = await this.$api.get({
          query: searchTerm,
          profileId,
          ranking: "relevant",
          page: 0,
        });

        const { nbHits, nbPages, page } = response?.data || {};

        if (response.data.hits.length > 0) {
          const fetchedAt = Date.now();
          const hits = response.data.hits.map((post) => ({ ...post, fetchedAt }));
          const meta = { nbHits, nbPages, page };
          const mappedPosts = mapData("objectId", hits);
          const postIds = hits.map(({ objectId }) => objectId);
          posts.value = mappedPosts;
          setSearchFeed({ feed: searchTerm, ids: postIds, meta });
        } else {
          posts.value = {};
          setSearchFeed({ feed: searchTerm, ids: [], meta: DEFAULT_META });
        }

        fetchState.value = { pending: false, error: null, timestamp: Date.now() };
      } catch (error) {
        fetchState.value = { pending: false, error: error as Error, timestamp: Date.now() };
        throw error;
      }
    };

    const $resetSearch = () => {
      postSearchFeedMap.value = {};
    };

    return {
      posts,
      fetchState,
      postSearchFeedMap,
      fetchBySearchTerm,
      fetchMoreBySearchTerm,
      getFeed,
      getHeroAspectRatio,
      feedHasMorePages,
      $resetSearch,
    };
  },
  {
    api: useCreatorShopApi,
  },
);
